// src/features/videos/FlashPlayerPage.tsx — Flash (videos of 60 s or less; owner renamed
// "Shorts" to Flash, 2026-09-26). v2 after the owner's concepts
// docs/References/Images/shorts_player.webp + shorts_player_panels.webp:
//
//   ← Previous  [ vertical player: Sound on · duration · title/creator/Follow/views/text ·
//                 controls · Next ↓ ]  Like · Dislike · Share · Quote · Save · More
//   right edge: "Up next" and "Comments" tabs — each opens a panel beside the player (both
//   can be open when there is room; on a narrow page opening one closes the other, and below
//   desktop they open as side sheets)
//
// Lives inside the app shell (it used to be a bare full-screen page) and borrows the shell's
// right column for itself, like the watch page's theater mode. ↑/↓, Page Up/Down, the mouse
// wheel and a vertical swipe move between Flashes; Escape goes back to Videos. A sample
// (seed) Flash keeps its labelled placeholder until real ones exist.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Avatar, Button, IconButton, MetaRow, Panel, Sheet, Switch, useToast } from '@/ui';
import { Icon } from '@/icons';
import { useWideCenter } from '@/app/shell/RightRail';
import {
  followButtonLabel,
  useFollow,
  usePostStatus,
  useRequireAuth,
  useSession,
  useVideos,
  useViewport,
} from '@/hooks';
import { useWriteGate } from '@/hooks/writeGate';
import { GeneratedThumb } from '@/components/GeneratedThumb/GeneratedThumb';
import { PlayGlyph } from '@/components/GeneratedThumb/PlayGlyph';
import { DisabledHint } from '@/ui';
import { formatCount } from '@/components/formatCount';
import { postsService } from '@/core/services/postsService';
import { useQuoteDraftStore } from '@/core/stores/quoteDraftStore';
import { useComposeSheetStore } from '@/core/stores/composeSheetStore';
import { SharePostDialog } from '@/components/PostCard/SharePostDialog';
import { EditPostDialog } from '@/components/PostCard/EditPostDialog';
import { DeletePostDialog } from '@/components/PostCard/DeletePostDialog';
import { useOwnPostActions } from '@/components/PostCard/useOwnPostActions';
import { ReportModal } from '@/components/ReportModal';
import { AppealModal } from '@/components/AppealModal';
import { PostStatus } from '@/components/PostCard/PostStatus';
import { CommentsSection } from '@/features/post-detail/CommentsSection';
import { DiscussionToolsProvider } from '@/features/discussion-tools/DiscussionToolsProvider';
import type { Post } from '@/core/models/post';
import type { VideoItem } from '@/core/models/video';
import { formatViews } from './meta';
import { flashPath } from './flashPath';
import { VideoPlayer, type VideoPlayerHandle } from './player/VideoPlayer';
import { PlayerPill } from './player/PlayerPill';
import { RailVideoItem } from './RailVideoItem';
import { useRealShorts } from './real/useRealVideos';
import { toCreator, toVideoItem, videoDescription, videoTitle } from './real/realItems';
import { RoundAction } from './real/watch/actionStyles';
import { SaveMenu } from './real/watch/SaveMenu';
import { VideoMoreMenu } from './real/watch/VideoMoreMenu';

const INK = '#fff';
const MUTED = 'rgba(255, 255, 255, 0.78)';
/** Below this page width both panels don't fit beside the player: one at a time. */
const BOTH_PANELS_MIN_WIDTH = 1240;
const WHEEL_COOLDOWN_MS = 650;

const Page = styled.div`
  position: relative;
  min-height: calc(100vh - 92px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px 72px 4px 12px;
  @media (max-width: 1023px) {
    padding: 0;
    min-height: 0;
  }
`;

const Stage = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
  @media (max-width: 1023px) {
    gap: 0;
    width: 100%;
    justify-content: center;
  }
`;

const Side = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`;

const PlayerCol = styled.div`
  position: relative;
  flex: 0 1 auto;
  min-width: 0;
`;

const Round = styled(RoundAction)``;

const NextOnPlayer = styled.div`
  position: absolute;
  right: 14px;
  top: 58%;
  z-index: 6;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  color: ${INK};
  font: 600 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
  text-align: center;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
  button {
    display: grid;
    place-items: center;
    width: 46px;
    height: 46px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: rgba(14, 10, 26, 0.55);
    color: ${INK};
    cursor: pointer;
    &:hover:not(:disabled) {
      background: rgba(40, 30, 64, 0.72);
    }
    &:disabled {
      opacity: 0.4;
      cursor: default;
    }
    &:focus-visible {
      outline: 2px solid ${({ theme }) => theme.color.focusRing};
    }
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-right: 64px;
  color: ${INK};
  h1 {
    margin: 0;
    font: 700 ${({ theme }) => theme.fontSize.md} ${({ theme }) => theme.font.display};
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.5);
    overflow-wrap: anywhere;
  }
  p {
    margin: 0;
    font: 400 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
    color: ${MUTED};
  }
`;

const Who = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  strong {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font: 700 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  }
  small {
    display: block;
    color: ${MUTED};
    font: 400 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
  }
`;

const Blurb = styled.p`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const SidePanel = styled(Panel)`
  width: 300px;
  flex-shrink: 0;
  align-self: stretch;
  max-height: min(calc(100vh - 110px), 860px);
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const PanelHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  h2 {
    margin: 0;
    font: 700 ${({ theme }) => theme.fontSize.md} ${({ theme }) => theme.font.display};
    color: ${({ theme }) => theme.color.textPrimary};
  }
  label {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;
    font: 500 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
    color: ${({ theme }) => theme.color.textMuted};
  }
`;

const EdgeTabs = styled.div`
  position: absolute;
  top: 70px;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
  z-index: 3;
`;

// The label is there for screen readers all along; sighted it "forms" on hover or keyboard
// focus: it fades in, sharpens and closes its letter-spacing while the tab widens leftwards.
const TabLabel = styled.span`
  font: 600 12px ${({ theme }) => theme.font.body};
  white-space: nowrap;
  opacity: 0;
  letter-spacing: 0.35em;
  filter: blur(4px);
  transform: translateX(6px);
  transition:
    opacity 220ms ease 60ms,
    letter-spacing 340ms cubic-bezier(0.2, 0.8, 0.2, 1) 40ms,
    filter 280ms ease 60ms,
    transform 300ms cubic-bezier(0.2, 0.8, 0.2, 1) 40ms;
  @media (prefers-reduced-motion: reduce) {
    letter-spacing: normal;
    filter: none;
    transform: none;
    transition: opacity 120ms linear;
  }
`;

// Concept shorts_player: a tall pill flush with the right edge, dark fill, violet gradient edge.
// Hover never recolours the edge (owner rule) — the tab widens and the fill lifts slightly.
const EdgeTab = styled.button`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  grid-template-rows: auto 1fr auto;
  justify-items: center;
  align-items: center;
  width: 46px;
  height: 128px;
  padding: 16px 0 12px;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  border-right: 0;
  border-radius: 18px 0 0 18px;
  background:
    linear-gradient(160deg, ${({ theme }) => theme.color.brandFrom}26, transparent 58%) padding-box,
    linear-gradient(${({ theme }) => theme.color.surface}, ${({ theme }) => theme.color.surface})
      padding-box;
  box-shadow: -8px 0 22px -14px ${({ theme }) => theme.color.brandFrom};
  color: ${({ theme }) => theme.color.textPrimary};
  overflow: hidden;
  cursor: pointer;
  transition:
    width 300ms cubic-bezier(0.2, 0.8, 0.2, 1),
    filter 200ms ease;
  svg:first-child {
    color: ${({ theme }) => theme.color.accentCyan};
  }
  svg:last-child {
    color: ${({ theme }) => theme.color.textMuted};
  }
  &:hover,
  &:focus-visible {
    width: 96px;
    filter: brightness(1.12);
  }
  &:hover ${TabLabel}, &:focus-visible ${TabLabel} {
    opacity: 1;
    letter-spacing: 0.01em;
    filter: none;
    transform: none;
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.focusRing};
    outline-offset: -3px;
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const SampleFrame = styled.div`
  position: relative;
  aspect-ratio: 9 / 16;
  height: min(calc(100vh - 110px), 860px);
  max-width: 100%;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  background: #000;
`;

const SampleOverlay = styled.div`
  position: absolute;
  inset: 0;
  display: grid;
  place-items: center;
`;

const SampleInfo = styled.div`
  position: absolute;
  inset: auto 0 0 0;
  padding: 24px 18px;
  background: linear-gradient(0deg, rgba(0, 0, 0, 0.75), transparent);
  color: ${INK};
  h1 {
    margin: 0 0 6px;
    font: 700 ${({ theme }) => theme.fontSize.md} ${({ theme }) => theme.font.display};
  }
`;

const Missing = styled.div`
  padding: 60px 16px;
  text-align: center;
  font-family: ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textMuted};
`;

function FollowOnPlayer({ post }: { post: Post }) {
  const { isFollowing, isRequested, toggleFollow } = useFollow(post.handle);
  const requireAuth = useRequireAuth();
  return (
    <Button
      size="sm"
      variant={isFollowing || isRequested ? 'secondary' : 'primary'}
      onClick={() => {
        if (requireAuth('follow')) void toggleFollow();
      }}
      aria-label={`${followButtonLabel(isFollowing, isRequested)} ${post.handle}`}
    >
      {followButtonLabel(isFollowing, isRequested)}
    </Button>
  );
}

export function FlashPlayerPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const seed = useVideos();
  // A sample id keeps the seed player; anything else is a real Flash.
  const isSample = seed.shorts.some((s) => s.id === id);
  const real = useRealShorts(seed.loaded && !isSample, id);
  const realPosts = useMemo(() => new Map(real.posts.map((p) => [p.id, p])), [real.posts]);
  const flashes: VideoItem[] = useMemo(
    () =>
      isSample || !seed.loaded
        ? seed.shorts
        : real.posts.flatMap((p) => {
            const item = toVideoItem(p);
            return item ? [item] : [];
          }),
    [isSample, seed.loaded, seed.shorts, real.posts],
  );
  const loaded = isSample ? seed.loaded : real.loaded;
  const index = useMemo(() => flashes.findIndex((s) => s.id === id), [flashes, id]);
  const current = flashes[index];
  const post = current ? realPosts.get(current.id) : undefined;

  useWideCenter(true);

  const goTo = useCallback(
    (nextIndex: number) => {
      const target = flashes[nextIndex];
      if (target) navigate(flashPath(target.id), { replace: true });
    },
    [navigate, flashes],
  );

  // ↑/↓, Page Up/Down, Escape — when nothing that takes typing is focused.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const t = e.target as HTMLElement | null;
      if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
      if (t?.closest?.('[role="dialog"], [role="menu"]')) return;
      if (e.key === 'Escape') navigate('/videos');
      else if (e.key === 'ArrowDown' || e.key === 'PageDown') {
        e.preventDefault();
        goTo(index + 1);
      } else if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        goTo(index - 1);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, goTo, navigate]);

  const lastWheel = useRef(0);
  function onWheel(e: React.WheelEvent) {
    if ((e.target as HTMLElement).closest?.('[data-flash-panel]')) return;
    const now = Date.now();
    if (now - lastWheel.current < WHEEL_COOLDOWN_MS || Math.abs(e.deltaY) < 20) return;
    lastWheel.current = now;
    goTo(index + (e.deltaY > 0 ? 1 : -1));
  }
  const touchY = useRef<number | null>(null);

  if (loaded && index === -1) {
    return (
      <Missing>
        <p>This Flash couldn&apos;t be found.</p>
        <Button variant="secondary" onClick={() => navigate('/videos')}>
          Back to Videos
        </Button>
      </Missing>
    );
  }
  if (!current) return <Missing role="status">Loading…</Missing>;

  return (
    <Page
      onWheel={onWheel}
      onTouchStart={(e) => (touchY.current = e.touches[0]?.clientY ?? null)}
      onTouchEnd={(e) => {
        if (touchY.current === null) return;
        const delta = touchY.current - (e.changedTouches[0]?.clientY ?? touchY.current);
        if (delta > 50) goTo(index + 1);
        else if (delta < -50) goTo(index - 1);
        touchY.current = null;
      }}
      role="region"
      aria-label={`Flash ${index + 1} of ${flashes.length}: ${current.title}`}
      data-testid="flash-page"
    >
      {post ? (
        <DiscussionToolsProvider key={post.id} post={post}>
          <RealFlash
            key={post.id}
            post={post}
            hasPrev={index > 0}
            hasNext={index < flashes.length - 1}
            onPrev={() => goTo(index - 1)}
            onNext={() => goTo(index + 1)}
            upNext={flashes.slice(index + 1).concat(flashes.slice(0, index))}
            posts={realPosts}
          />
        </DiscussionToolsProvider>
      ) : (
        <SampleFlash
          item={current}
          hasPrev={index > 0}
          hasNext={index < flashes.length - 1}
          onPrev={() => goTo(index - 1)}
          onNext={() => goTo(index + 1)}
        />
      )}
    </Page>
  );
}

interface NavProps {
  hasPrev: boolean;
  hasNext: boolean;
  onPrev: () => void;
  onNext: () => void;
}

function PrevButton({ hasPrev, onPrev }: Pick<NavProps, 'hasPrev' | 'onPrev'>) {
  return (
    <Round type="button" onClick={onPrev} disabled={!hasPrev} aria-label="Previous Flash">
      <span>
        <Icon name="arrow-up" size={22} />
      </span>
      <span>Previous</span>
    </Round>
  );
}

function NextButton({ hasNext, onNext }: Pick<NavProps, 'hasNext' | 'onNext'>) {
  return (
    <NextOnPlayer>
      <button type="button" onClick={onNext} disabled={!hasNext} aria-label="Next Flash">
        <Icon name="arrow-down" size={22} />
      </button>
      <span aria-hidden="true">Next</span>
    </NextOnPlayer>
  );
}

function RealFlash({
  post,
  upNext,
  posts,
  ...nav
}: NavProps & { post: Post; upNext: VideoItem[]; posts: Map<string, Post> }) {
  const { currentUser, isAuthenticated, updateProfile } = useSession();
  const requireAuth = useRequireAuth();
  const gate = useWriteGate();
  const toast = useToast();
  const navigate = useNavigate();
  const own = useOwnPostActions(post);
  const status = usePostStatus(post.id);
  const isDesktop = useViewport() === 'desktop';
  const setPendingQuote = useQuoteDraftStore((s) => s.setPendingQuote);
  const openComposeSheet = useComposeSheetStore((s) => s.openSheet);
  const playerRef = useRef<VideoPlayerHandle>(null);
  const pageRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState({ upNext: false, comments: false });
  const [sharing, setSharing] = useState(false);
  const [reporting, setReporting] = useState(false);
  const [appealingCaseId, setAppealingCaseId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [guestAutoplay, setGuestAutoplay] = useState(false);
  const autoplay = isAuthenticated ? Boolean(currentUser?.autoplayNext) : guestAutoplay;
  const video = post.video!;
  const title = videoTitle(post);
  const description = videoDescription(post);
  const next = upNext[0];

  function toggle(which: 'upNext' | 'comments') {
    setOpen((o) => {
      const opening = !o[which];
      const width = pageRef.current?.parentElement?.clientWidth ?? 0;
      // Not enough room for both beside the player: one at a time.
      if (opening && isDesktop && width < BOTH_PANELS_MIN_WIDTH) {
        return { upNext: which === 'upNext', comments: which === 'comments' };
      }
      return { ...o, [which]: opening };
    });
  }

  function changeAutoplay(nextValue: boolean) {
    if (!isAuthenticated) {
      setGuestAutoplay(nextValue);
      return;
    }
    void updateProfile({ autoplayNext: nextValue }).then((ok) => {
      if (!ok) toast('Something went wrong');
    });
  }

  async function react(kind: 'like' | 'dislike') {
    if (!requireAuth(kind)) return;
    setBusy(true);
    try {
      const done =
        kind === 'like'
          ? await postsService.toggleCheer(post.id)
          : await postsService.toggleDislike(post.id);
      if (!done) toast('Something went wrong');
    } catch {
      toast('Something went wrong');
    } finally {
      setBusy(false);
    }
  }

  const upNextList = (
    <>
      <PanelHead>
        <h2>Up next</h2>
        <label>
          Autoplay
          <Switch checked={autoplay} onChange={changeAutoplay} label="Autoplay the next Flash" />
        </label>
        {isDesktop && (
          <IconButton
            icon="close"
            label="Close Up next"
            size={16}
            onClick={() => toggle('upNext')}
          />
        )}
      </PanelHead>
      {upNext.length === 0 ? (
        <p>No more Flashes yet.</p>
      ) : (
        <List>
          {upNext.map((v) => {
            const p = posts.get(v.id);
            return (
              <li key={v.id}>
                <RailVideoItem
                  video={v}
                  creator={p ? toCreator(p) : undefined}
                  to={flashPath(v.id)}
                />
              </li>
            );
          })}
        </List>
      )}
    </>
  );

  const comments = (
    <>
      {isDesktop && (
        <PanelHead>
          <h2>Comments</h2>
          <IconButton
            icon="close"
            label="Close comments"
            size={16}
            onClick={() => toggle('comments')}
          />
        </PanelHead>
      )}
      <CommentsSection postId={post.id} />
    </>
  );

  const overlay = (
    <Info>
      <h1>{title}</h1>
      <Who>
        <Avatar name={post.name} avatarKey={post.av} size={40} />
        <span style={{ minWidth: 0 }}>
          <strong>
            {post.name}
            {post.verified && <Icon name="verify" size={14} />}
          </strong>
          <small>{post.handle}</small>
        </span>
        {!own.isOwn && <FollowOnPlayer post={post} />}
      </Who>
      <p>
        <MetaRow>
          <span>{formatViews(video.views ?? 0)}</span>
          <span>{post.time}</span>
        </MetaRow>
      </p>
      {description && <Blurb>{description}</Blurb>}
    </Info>
  );

  const actions = (
    <Side role="group" aria-label="Flash actions">
      <Round
        type="button"
        $on={post.liked}
        aria-pressed={post.liked}
        aria-label={`Cherish, ${post.cheers} cherishes`}
        disabled={busy}
        onClick={() => void react('like')}
      >
        <span>
          <Icon name="cherish" size={22} />
        </span>
        <span>{formatCount(post.cheers)}</span>
      </Round>
      <Round
        type="button"
        $on={Boolean(post.disliked)}
        aria-pressed={Boolean(post.disliked)}
        aria-label={`Dislike, ${post.dislikes ?? 0} dislikes`}
        disabled={busy}
        onClick={() => void react('dislike')}
      >
        <span>
          <Icon name="thumbs-down" size={22} />
        </span>
        <span>{post.dislikes ? formatCount(post.dislikes) : 'Dislike'}</span>
      </Round>
      <Round
        type="button"
        onClick={() => {
          if (gate.allow('share')) setSharing(true);
        }}
      >
        <span>
          <Icon name="share" size={22} />
        </span>
        <span>Share</span>
      </Round>
      <Round
        type="button"
        onClick={() => {
          if (!requireAuth('quote')) return;
          setPendingQuote(post);
          openComposeSheet();
        }}
      >
        <span>
          <Icon name="quote" size={22} />
        </span>
        <span>Quote</span>
      </Round>
      <SaveMenu post={post} round />
      {!isDesktop && (
        <>
          <Round type="button" aria-expanded={open.comments} onClick={() => toggle('comments')}>
            <span>
              <Icon name="message-text" size={22} />
            </span>
            <span>Comments</span>
          </Round>
          <Round type="button" aria-expanded={open.upNext} onClick={() => toggle('upNext')}>
            <span>
              <Icon name="chapters" size={22} />
            </span>
            <span>Up next</span>
          </Round>
        </>
      )}
      <VideoMoreMenu
        post={post}
        title={title}
        isOwn={own.isOwn}
        nextHref={next ? flashPath(next.id) : '/videos'}
        currentTime={() => playerRef.current?.currentTime() ?? 0}
        onReport={() => {
          if (requireAuth('report')) setReporting(true);
        }}
        onEdit={own.openEdit}
        onDelete={own.openDelete}
        trigger={
          <Round type="button" aria-label="More video actions">
            <span>
              <Icon name="more-horizontal" size={22} />
            </span>
            <span>More</span>
          </Round>
        }
      />
    </Side>
  );

  return (
    <>
      <Stage ref={pageRef}>
        {isDesktop && (
          <Side>
            <PrevButton hasPrev={nav.hasPrev} onPrev={nav.onPrev} />
          </Side>
        )}
        <PlayerCol>
          <VideoPlayer
            ref={playerRef}
            key={post.id}
            variant="flash"
            video={video}
            title={title}
            autoPlay
            loopByDefault
            autoplayNext={autoplay}
            onAutoplayNextChange={changeAutoplay}
            pageShortcuts
            reserveVerticalArrows
            overlay={overlay}
            onEnded={() => {
              if (autoplay && next) navigate(flashPath(next.id), { replace: true });
            }}
          />
          {/* Below desktop a swipe moves on; the button would sit on the action column. */}
          {isDesktop && <NextButton hasNext={nav.hasNext} onNext={nav.onNext} />}
          <PostStatus
            status={status}
            onAppeal={(caseId) => {
              if (requireAuth('appeal')) setAppealingCaseId(caseId);
            }}
          />
        </PlayerCol>
        {isDesktop && actions}
        {isDesktop && open.upNext && <SidePanel data-flash-panel>{upNextList}</SidePanel>}
        {isDesktop && open.comments && <SidePanel data-flash-panel>{comments}</SidePanel>}
      </Stage>

      {!isDesktop && (
        <div style={{ position: 'absolute', right: 10, bottom: 120, zIndex: 7 }}>{actions}</div>
      )}

      {isDesktop && (!open.upNext || !open.comments) && (
        <EdgeTabs>
          {!open.upNext && (
            <EdgeTab type="button" aria-expanded={open.upNext} onClick={() => toggle('upNext')}>
              <Icon name="play" size={18} />
              <TabLabel>Up next</TabLabel>
              <Icon name="chevron-right" size={14} />
            </EdgeTab>
          )}
          {!open.comments && (
            <EdgeTab type="button" aria-expanded={open.comments} onClick={() => toggle('comments')}>
              <Icon name="message-text" size={18} />
              <TabLabel>Comments</TabLabel>
              <Icon name="chevron-right" size={14} />
            </EdgeTab>
          )}
        </EdgeTabs>
      )}

      {!isDesktop && (
        <>
          <Sheet side="right" open={open.upNext} onClose={() => toggle('upNext')} title="Up next">
            <div data-flash-panel>{upNextList}</div>
          </Sheet>
          <Sheet
            side="right"
            open={open.comments}
            onClose={() => toggle('comments')}
            title="Comments"
          >
            <div data-flash-panel>{comments}</div>
          </Sheet>
        </>
      )}

      {sharing && (
        <SharePostDialog
          post={post}
          url={`${window.location.origin}${flashPath(post.id)}`}
          onClose={() => setSharing(false)}
        />
      )}
      {reporting && (
        <ReportModal target={{ kind: 'post', post }} onClose={() => setReporting(false)} />
      )}
      {appealingCaseId && (
        <AppealModal caseId={appealingCaseId} onClose={() => setAppealingCaseId(null)} />
      )}
      {own.editing && (
        <EditPostDialog
          post={post}
          onClose={own.closeEdit}
          onSave={own.saveEdit}
          editWindowAdvisory={own.editWindowAdvisory}
        />
      )}
      <DeletePostDialog
        open={own.deleting}
        onCancel={own.closeDelete}
        onConfirm={() => void own.confirmDelete()}
      />
    </>
  );
}

function SampleFlash({ item, ...nav }: NavProps & { item: VideoItem }) {
  const isDesktop = useViewport() === 'desktop';
  return (
    <Stage>
      {isDesktop && (
        <Side>
          <PrevButton hasPrev={nav.hasPrev} onPrev={nav.onPrev} />
        </Side>
      )}
      <PlayerCol>
        <SampleFrame>
          <GeneratedThumb seedKey={item.id} fill />
          <SampleOverlay>
            <DisabledHint explanation="Playback isn't available yet. This is a sample.">
              {(describedBy) => <PlayGlyph describedBy={describedBy} size={72} tone="overlay" />}
            </DisabledHint>
          </SampleOverlay>
          <div style={{ position: 'absolute', top: 12, left: 12 }}>
            <PlayerPill as="span" data-testid="sample-badge">
              Sample
            </PlayerPill>
          </div>
          <SampleInfo>
            <h1>{item.title}</h1>
          </SampleInfo>
        </SampleFrame>
        <NextButton hasNext={nav.hasNext} onNext={nav.onNext} />
      </PlayerCol>
    </Stage>
  );
}

/** /videos/shorts/:id (before the rename) → /videos/flash/:id, so shared links keep working. */
export function ShortsRedirect() {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={flashPath(id ?? '')} replace />;
}
